/**
 * 逻辑流策略编辑器 - 主页面
 * 使用 React Flow 实现可拖拽的逻辑流图编辑
 */

import React, {useCallback, useEffect, useState} from 'react';
import {useNavigate, useParams} from 'react-router-dom';
import {Alert, App, Drawer, Empty, List, Modal, Spin, Tag, Typography} from 'antd';
import LogicFlowCanvas from '../../components/logicFlow/LogicFlowCanvas';
import NodePalette from '../../components/logicFlow/panels/NodePalette';
import NodeConfigPanel from '../../components/logicFlow/panels/NodeConfigPanel';
import StrategyConfigPanel from '../../components/logicFlow/panels/StrategyConfigPanel';
import CodePreviewPanel from '../../components/logicFlow/panels/CodePreviewPanel';
import LogicFlowToolbar from '../../components/logicFlow/toolbar/LogicFlowToolbar';
import {useLogicFlowEditor} from '../../hooks/useLogicFlowEditor';
import {detectSchemaVersion, migrateV1ToV2} from '../../utils/visualModelMigration';
import type {MyStrategyItem, StrategyVisualModel} from '../../services/myStrategyService';
import {myStrategyService} from '../../services/myStrategyService';
import type {LogicFlowNodeType, StrategyVisualModelV2} from '../../types/logicFlow';
import type {ValidationResult} from '../../store/logicFlowStore';
import styles from './LogicFlowEditorPage.module.css';

const {Text} = Typography;

const LogicFlowEditorPage: React.FC = () => {
    const {message: messageApi} = App.useApp();
    const navigate = useNavigate();
    const {id: idParam} = useParams<{ id: string }>();
    const {message} = App.useApp();

    const editor = useLogicFlowEditor();

    // UI 状态
    const [historyOpen, setHistoryOpen] = useState(false);
    const [historyLoading, setHistoryLoading] = useState(false);
    const [historyList, setHistoryList] = useState<MyStrategyItem[]>([]);
    const [rightPanel, setRightPanel] = useState<'config' | 'node' | 'code'>('config');
    const [migrationModalOpen, setMigrationModalOpen] = useState(false);
    const [migrationModel, setMigrationModel] = useState<StrategyVisualModelV2 | null>(null);
    const [validationModalOpen, setValidationModalOpen] = useState(false);
    const [validationModalResult, setValidationModalResult] = useState<ValidationResult | null>(null);

    // 从 URL 参数加载策略
    useEffect(() => {
        const strategyId = idParam;
        if (strategyId) {
            loadStrategy(parseInt(strategyId, 10));
        } else {
            editor.resetToDefault();
        }
    }, [idParam]);

    const loadStrategy = useCallback(async (id: number) => {
        try {
            const detail = await myStrategyService.getMyStrategyById(id);

            // 仅原创策略可编辑
            if ('FOLLOW' === detail.sourceType) {
                messageApi.warning('跟随策略不可编辑，仅原创策略支持编辑');
                return;
            }

            const visResp = await myStrategyService.getStrategyVisualModel(id);

            // 尝试解析已有可视化模型
            let visualModelJson: any = null;
            try {
                visualModelJson = JSON.parse(visResp.visualModel);
            } catch {
                // 可视化模型为空或格式错误，继续从源码转换
            }

            const version = detectSchemaVersion(visualModelJson);
            console.log('[loadStrategy] id=%d version=%s hasCode=%s sourceType=%s',
                id, version, !!detail.strategyCode, detail.sourceType);

            if ('2.0' === version) {
                // 已是 V2，直接加载
                editor.loadModel(visualModelJson as StrategyVisualModelV2, id);
                editor.setMetaInfo({
                    sourceType: detail.sourceType,
                    prompt: detail.prompt || '',
                    isActive: 1 === detail.isActive,
                });
                return;
            }

            // 获取源码：优先 detail.strategyCode，回退到 V1 模型的 rawCode
            const sourceCode = detail.strategyCode?.trim() || visualModelJson?.rawCode?.trim() || '';
            console.log('[loadStrategy] sourceCode length=%d', sourceCode.length);

            if (sourceCode) {
                try {
                    const v2ModelJson = await myStrategyService.convertCodeToV2(sourceCode);
                    const v2Model = JSON.parse(v2ModelJson) as StrategyVisualModelV2;
                    const nodeCount = v2Model.graph?.nodes?.length || 0;
                    const edgeCount = v2Model.graph?.edges?.length || 0;
                    console.log('[loadStrategy] V2转换成功 nodes=%d edges=%d', nodeCount, edgeCount);

                    editor.loadModel(v2Model, id);
                    editor.setMetaInfo({
                        sourceType: detail.sourceType,
                        prompt: detail.prompt || '',
                        isActive: 1 === detail.isActive,
                    });
                    const warnings = (v2Model as any)._warnings as string[] | undefined;
                    const warnMsg = warnings?.length ? ' 警告: ' + warnings.join('; ') : '';
                    if (nodeCount > 0) {
                        messageApi.success(`已解析${nodeCount}个节点、${edgeCount}条连线${warnMsg}`);
                    } else {
                        messageApi.warning(`未识别到任何节点${warnMsg}`);
                    }
                    return;
                } catch (convErr: any) {
                    console.error('[loadStrategy] V2转换失败', convErr);
                    messageApi.warning('从源码解析失败: ' + convErr.message + '，尝试旧版迁移...');
                }
            } else {
                console.warn('[loadStrategy] 无可用源码');
            }

            // 回退：V1 可视化模型迁移
            if ('1.0' === version) {
                const migration = migrateV1ToV2(visualModelJson as StrategyVisualModel);
                setMigrationModel(migration.model);
                setMigrationModalOpen(true);
                editor.setMetaInfo({
                    sourceType: detail.sourceType,
                    prompt: detail.prompt || '',
                    isActive: 1 === detail.isActive,
                });
            } else {
                messageApi.warning('无法识别策略版本且无可用源码（该策略可能为跟随类型，源码不可见）');
            }
        } catch (e: any) {
            console.error('[loadStrategy] 加载失败', e);
            messageApi.error(`加载策略失败: ${e.message}`);
        }
    }, [editor, message]);

    const handleMigrationConfirm = useCallback(() => {
        if (migrationModel) {
            editor.loadModel(migrationModel);
            messageApi.info('已迁移为逻辑流模式，请检查节点和连线后保存');
        }
        setMigrationModalOpen(false);
        setMigrationModel(null);
    }, [migrationModel, editor, message]);

    const handleMigrationCancel = useCallback(() => {
        setMigrationModalOpen(false);
        setMigrationModel(null);
        navigate('/strategy/list');
    }, [navigate]);

    // 保存
    const handleSave = useCallback(async () => {
        const result = await editor.save();
        if (result.success) {
            messageApi.success(`保存成功 #${result.strategyId}`);
            navigate(`/strategy/logic-flow/${result.strategyId}`);
        } else {
            messageApi.error(result.errors?.join('; ') || '保存失败');
        }
    }, [editor, message, navigate]);

    // 校验（弹窗显示结果）
    const handleValidate = useCallback(async () => {
        const result = await editor.validate();
        setValidationModalResult(result);
        setValidationModalOpen(true);
    }, [editor]);

    // 新建：先创建策略获取 ID，再跳转到带 ID 的路由
    const handleNew = useCallback(() => {
        const doCreate = async () => {
            editor.resetToDefault();
            try {
                const result = await myStrategyService.createMyStrategy({title: '未命名策略'});
                navigate(`/strategy/logic-flow/${result.id}`, {replace: true});
            } catch (e: any) {
                messageApi.error('创建策略失败: ' + (e.message || '未知错误'));
            }
        };
        if (editor.isDirty) {
            Modal.confirm({
                title: '未保存的更改',
                content: '当前策略有未保存的更改，是否放弃并新建？',
                okText: '放弃并新建',
                cancelText: '取消',
                onOk: () => doCreate(),
            });
        } else {
            doCreate();
        }
    }, [editor, navigate, message]);

    // 加载历史列表
    const handleOpenHistory = useCallback(async () => {
        setHistoryOpen(true);
        setHistoryLoading(true);
        try {
            const resp = await myStrategyService.getMyStrategies(0, 30, 'original');
            setHistoryList(resp.list || []);
        } catch {
            setHistoryList([]);
        } finally {
            setHistoryLoading(false);
        }
    }, []);

    const handleLoadHistory = useCallback(async (strategyId: number) => {
        setHistoryOpen(false);
        await loadStrategy(strategyId);
    }, [loadStrategy]);

    // 自动布局
    const handleAutoLayout = useCallback(() => {
        editor.autoLayout();
    }, [editor.autoLayout]);

    // 节点操作
    const handleAddNode = useCallback(
        (type: LogicFlowNodeType) => {
            editor.addNode(type);
            setRightPanel('node');
        },
        [editor.addNode]
    );

    const handleRemoveNode = useCallback(
        (id: string) => {
            editor.removeNode(id);
        },
        [editor.removeNode]
    );

    const handleUpdateNodeConfig = useCallback(
        (id: string, config: Record<string, unknown>) => {
            editor.updateNodeConfig(id, config);
        },
        [editor.updateNodeConfig]
    );

    const handleSelectNode = useCallback(
        (id: string | null) => {
            editor.selectNode(id);
            if (id) {
                setRightPanel(prev => 'node' === prev ? prev : 'node');
            }
        },
        [editor.selectNode]
    );

    const handleDropNewNode = useCallback(
        (type: string) => {
            editor.addNode(type as LogicFlowNodeType);
        },
        [editor.addNode]
    );

    const selectedNode = editor.getSelectedNode();

    return (
        <div className={styles.page}>
            {/* 工具栏 */}
            <LogicFlowToolbar
                isDirty={editor.isDirty}
                isSaving={editor.isSaving}
                strategyId={editor.strategyId}
                strategyName={editor.visualModel.strategyName}
                viewMode={editor.viewMode}
                onSave={handleSave}
                onValidate={handleValidate}
                onNew={handleNew}
                onOpenHistory={handleOpenHistory}
                onAutoLayout={handleAutoLayout}
                onViewModeChange={editor.setViewMode}
            />

            {/* 主体 */}
            {'flow' === editor.viewMode ? (
                <div className={styles.body}>
                    {/* 左侧节点面板 */}
                    <NodePalette onAddNode={handleAddNode}/>

                    {/* 中央画布 */}
                    <div className={styles.canvasArea}>
                        <LogicFlowCanvas
                            lfNodes={editor.visualModel.graph.nodes}
                            lfEdges={editor.visualModel.graph.edges}
                            selectedNodeId={editor.selectedNodeId}
                            onSelectNode={handleSelectNode}
                            onConnect={editor.addEdge}
                            onRemoveNode={handleRemoveNode}
                            onDropNewNode={handleDropNewNode}
                            canAddEdge={editor.canAddEdge}
                        />
                    </div>

                    {/* 右侧面板 */}
                    <div className={styles.rightPanel}>
                        <div className={styles.panelTabs}>
                            <button
                                className={`${styles.panelTab} ${'config' === rightPanel ? styles.panelTabActive : ''}`}
                                onClick={() => setRightPanel('config')}
                            >
                                策略配置
                            </button>
                            <button
                                className={`${styles.panelTab} ${'node' === rightPanel ? styles.panelTabActive : ''}`}
                                onClick={() => setRightPanel('node')}
                            >
                                节点属性
                            </button>
                            <button
                                className={`${styles.panelTab} ${'code' === rightPanel ? styles.panelTabActive : ''}`}
                                onClick={() => setRightPanel('code')}
                            >
                                代码预览
                            </button>
                        </div>
                        <div className={styles.panelContent}>
                            {'config' === rightPanel && (
                                <StrategyConfigPanel
                                    config={editor.visualModel.config}
                                    strategyName={editor.visualModel.strategyName}
                                    title={editor.visualModel.title}
                                    description={editor.visualModel.description}
                                    onUpdate={editor.updateConfig}
                                    onUpdateMeta={editor.updateMeta}
                                />
                            )}
                            {'node' === rightPanel && (
                                <NodeConfigPanel
                                    node={selectedNode}
                                    onUpdateConfig={handleUpdateNodeConfig}
                                    onUpdateLabel={editor.updateNodeLabel}
                                    onRemoveNode={handleRemoveNode}
                                    onAddEdge={editor.addEdge}
                                    onRemoveEdge={editor.removeEdge}
                                    graph={editor.visualModel.graph}
                                />
                            )}
                            {'code' === rightPanel && (
                                <CodePreviewPanel
                                    strategyCode={editor.generatedCode}
                                    validationResult={editor.validationResult}
                                    onRefresh={editor.validate}
                                />
                            )}
                        </div>
                    </div>
                </div>
            ) : (
                <div className={styles.body}>
                    <div className={styles.codeFullArea}>
                        <CodePreviewPanel
                            strategyCode={editor.generatedCode}
                            validationResult={editor.validationResult}
                            onRefresh={editor.validate}
                        />
                    </div>
                </div>
            )}

            {/* 历史策略 Drawer */}
            <Drawer
                title="我的策略"
                placement="right"
                width={360}
                open={historyOpen}
                onClose={() => setHistoryOpen(false)}
                styles={{
                    header: {
                        background: 'rgba(12,17,32,0.98)',
                        borderBottom: '1px solid rgba(255,255,255,0.08)',
                        color: 'var(--pt-text-primary)'
                    },
                    body: {background: 'rgba(12,17,32,0.95)', padding: '16px'},
                }}
            >
                {historyLoading ? (
                    <div style={{textAlign: 'center', padding: '40px 0'}}>
                        <Spin/>
                    </div>
                ) : historyList.length === 0 ? (
                    <Empty description="暂无策略"/>
                ) : (
                    <List
                        dataSource={historyList}
                        renderItem={(item) => (
                            <div
                                key={item.id}
                                style={{
                                    padding: '12px',
                                    marginBottom: '8px',
                                    borderRadius: '10px',
                                    border: '1px solid rgba(255,255,255,0.08)',
                                    background: 'rgba(255,255,255,0.02)',
                                    cursor: 'pointer',
                                    transition: 'all 0.2s',
                                }}
                                onClick={() => handleLoadHistory(item.id)}
                            >
                                <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center'}}>
                                    <Text strong style={{color: 'var(--pt-text-primary)', fontSize: '13px'}}>
                                        {item.title || '未命名'}
                                    </Text>
                                    <Tag color={item.sourceType === 'FOLLOW' ? 'blue' : 'gold'}
                                         style={{fontSize: '10px'}}>
                                        {item.sourceType === 'FOLLOW' ? '跟随' : '原创'}
                                    </Tag>
                                </div>
                                {item.description && (
                                    <Text type="secondary" style={{fontSize: '12px'}}>
                                        {item.description.substring(0, 60)}{item.description.length > 60 ? '...' : ''}
                                    </Text>
                                )}
                                <div style={{marginTop: '6px'}}>
                                    <Text type="secondary" style={{fontSize: '11px'}}>
                                        {item.updatedAt || item.createdAt || ''}
                                    </Text>
                                </div>
                            </div>
                        )}
                    />
                )}
            </Drawer>

            {/* 校验结果 Modal */}
            <Modal
                title="模型校验结果"
                open={validationModalOpen}
                onCancel={() => setValidationModalOpen(false)}
                footer={null}
                width={520}
                styles={{
                    header: {
                        background: 'rgba(12,17,32,0.98)',
                        color: 'var(--pt-text-primary)',
                        borderBottom: '1px solid rgba(255,255,255,0.08)'
                    },
                    body: {background: 'rgba(12,17,32,0.95)', padding: '20px'},
                }}
            >
                {validationModalResult && (
                    <div style={{color: 'var(--pt-text-primary)'}}>
                        <div style={{marginBottom: '16px'}}>
                            {validationModalResult.valid ? (
                                <Tag color="green" style={{fontSize: '13px'}}>校验通过</Tag>
                            ) : (
                                <Tag color="red" style={{fontSize: '13px'}}>校验失败</Tag>
                            )}
                        </div>
                        {validationModalResult.errors.length > 0 && (
                            <div style={{marginBottom: '12px'}}>
                                {validationModalResult.errors.map((err, i) => (
                                    <Alert key={`e${i}`} type="error" message={err} style={{marginTop: '4px'}}
                                           showIcon/>
                                ))}
                            </div>
                        )}
                        {validationModalResult.warnings.length > 0 && (
                            <div>
                                {validationModalResult.warnings.map((warn, i) => (
                                    <Alert key={`w${i}`} type="warning" message={warn} style={{marginTop: '4px'}}
                                           showIcon/>
                                ))}
                            </div>
                        )}
                        {0 === validationModalResult.errors.length && 0 === validationModalResult.warnings.length && (
                            <Text type="secondary" style={{fontSize: '13px'}}>无错误或警告</Text>
                        )}
                    </div>
                )}
            </Modal>

            {/* 迁移确认 Modal */}
            <Modal
                title="策略格式迁移"
                open={migrationModalOpen}
                onOk={handleMigrationConfirm}
                onCancel={handleMigrationCancel}
                okText="确认迁移"
                cancelText="返回列表"
                styles={{
                    header: {background: 'rgba(12,17,32,0.98)', color: 'var(--pt-text-primary)'},
                    body: {background: 'rgba(12,17,32,0.95)'},
                }}
            >
                <div style={{color: 'var(--pt-text-primary)'}}>
                    <p>此策略使用旧版 Freqtrade 方法模式，需要迁移为新的逻辑流模式。</p>
                    <ul style={{fontSize: '13px', color: 'var(--pt-text-secondary)'}}>
                        <li>固定5节点将转换为动态节点</li>
                        <li>条件表达式将自动解析为结构化条件</li>
                        <li>复杂嵌套条件可能需要手动调整</li>
                        <li>迁移后请检查节点和连线是否正确</li>
                    </ul>
                    <p style={{color: 'var(--pt-primary)', fontSize: '12px'}}>
                        提示：迁移后需要点击"保存"才会持久化到数据库。
                    </p>
                </div>
            </Modal>
        </div>
    );
};

export default LogicFlowEditorPage;
